import {
  AI_OBSERVATORY_LINK,
  AI_OBSERVATORY_PAPER_LINK,
  DATA_REPO_LINK,
  ECONOMIES_PAPER_LINK,
  OPEN_MODEL_DASHBOARD_LINK,
} from "../constants";

export type ResearchLink = {
  label: string;
  href: string;
  external?: boolean;
  comingSoon?: boolean;
};

export type ResearchMetric = {
  value: string;
  label: string;
};

export type ResearchProgram = {
  title: string;
  label: string;
  description: string;
  href: string;
  cta: string;
  external?: boolean;
  comingSoon?: boolean;
  metrics: ResearchMetric[];
  accent: string;
  scope: string;
};

export type ResearchOutput = {
  year: string;
  title: string;
  description: string;
  href: string;
  external?: boolean;
  comingSoon?: boolean;
  tag: string;
};

export type ResearchDashboard = {
  title: string;
  label: string;
  metric: string;
  detail: string;
  description: string;
  href: string;
  external?: boolean;
  comingSoon?: boolean;
  accent: string;
  cta: string;
};

export type ImpactSignal = {
  value: string;
  label: string;
  detail: string;
};

export type CommunitySignal = {
  label: string;
  text: string;
};

export const researchDashboards: ResearchDashboard[] = [
  {
    title: "Data Provenance Explorer",
    label: "Training data",
    metric: "4,000+ datasets",
    detail: "608 languages across text, speech, and video",
    description:
      "Inspect dataset sources, licenses, creators, geographies, modalities, and derivation chains.",
    href: "/data-provenance-explorer/dataset-explorer",
    accent: "#A1DDFF",
    cta: "Explore datasets",
  },
  {
    title: "Web Consent Explorer",
    label: "Web data",
    metric: "14,000 domains",
    detail: "Terms, robots.txt, and crawl restrictions",
    description:
      "Track how web consent signals are changing as AI developers compete for training data.",
    href: "/data-provenance-explorer/web-data-explorer",
    accent: "#AC482A",
    cta: "Explore web consent",
  },
  {
    title: "Open Model Evolution",
    label: "Model ecosystem",
    metric: "851k open models",
    detail: "2.2B downloads and 200+ model attributes",
    description:
      "Monitor concentration, participation, transparency, and technical shifts in open-weight AI.",
    href: OPEN_MODEL_DASHBOARD_LINK,
    external: true,
    accent: "#ABDF75",
    cta: "View dashboard",
  },
  {
    title: "The AI Observatory",
    label: "Real-world use",
    metric: "23,158 conversations",
    detail: "85,633 turns from 7 public sources",
    description:
      "Compare how people use and misuse AI systems across sources, models, and contexts.",
    href: AI_OBSERVATORY_LINK,
    external: true,
    accent: "#D59DB3",
    cta: "Open Observatory",
  },
];

export const impactSignals: ImpactSignal[] = [
  {
    value: "4,000+",
    label: "datasets audited",
    detail:
      "Sources, licenses, creators, modalities, languages, and geographies",
  },
  {
    value: "14,000",
    label: "web domains tracked",
    detail: "Terms, robots.txt, crawl restrictions, and consent signals",
  },
  {
    value: "851k",
    label: "open models measured",
    detail:
      "Downloads, transparency, participation, concentration, and attributes",
  },
  {
    value: "23,158",
    label: "AI conversations annotated",
    detail: "145 features across public real-use conversation sources",
  },
];

export const communitySignals: CommunitySignal[] = [
  {
    label: "Public-interest infrastructure",
    text: "DPI releases papers, datasets, and dashboards that help researchers, journalists, policymakers, and builders reason from shared evidence.",
  },
  {
    label: "Policy and governance relevance",
    text: "Our work has been cited in public AI safety and governance reporting and used to ground debates about data transparency, consent, and market concentration.",
  },
  {
    label: "Featured coverage",
    text: "DPI findings have been covered by the Financial Times, The New York Times, The Washington Post, MIT Technology Review, IEEE Spectrum, and VentureBeat.",
  },
];

export const featuredInstitutions = [
  "Financial Times",
  "The New York Times",
  "The Washington Post",
  "MIT Technology Review",
  "IEEE Spectrum",
  "GOV.UK",
];

export const researchPrograms: ResearchProgram[] = [
  {
    title: "Training Data Provenance",
    label: "Dataset audits",
    description:
      "Trace the sources, licenses, creators, languages, geographies, and derivation chains behind public AI training datasets.",
    href: "/data-provenance-explorer/dataset-explorer",
    cta: "Explore datasets",
    metrics: [
      { value: "4,000+", label: "datasets" },
      { value: "608", label: "languages" },
    ],
    accent: "#A1DDFF",
    scope: "Origin, licensing, modality, language, geography",
  },
  {
    title: "Web Consent and the Data Commons",
    label: "Web data",
    description:
      "Measure how web domains, terms of service, and consent signals are changing as AI developers compete for training data.",
    href: "/data-provenance-explorer/web-data-explorer",
    cta: "Explore web consent",
    metrics: [
      { value: "14,000", label: "domains" },
      { value: "Consent", label: "signals" },
    ],
    accent: "#AC482A",
    scope: "Terms, robots.txt, crawl restrictions, consent",
  },
  {
    title: "Open Model Economy",
    label: "Model ecosystem",
    description:
      "Monitor concentration, participation, transparency, and model attributes across the open-weight AI ecosystem.",
    href: OPEN_MODEL_DASHBOARD_LINK,
    cta: "View the dashboard",
    external: true,
    metrics: [
      { value: "851k", label: "models" },
      { value: "2.2B", label: "downloads" },
    ],
    accent: "#ABDF75",
    scope: "Market share, participation, transparency, attributes",
  },
  {
    title: "AI Observatory",
    label: "Real-world AI use",
    description:
      "Study how people use and misuse AI systems across conversation sources, models, time periods, and user contexts.",
    href: AI_OBSERVATORY_LINK,
    cta: "Open Observatory",
    external: true,
    metrics: [
      { value: "7", label: "sources" },
      { value: "145", label: "features" },
    ],
    accent: "#D59DB3",
    scope: "Tasks, sensitive use, personas, turns, model behavior",
  },
];

export const homepageFindings = [
  {
    label: "Data transparency",
    value: "Open weights now outpace truly open-source models",
    source: "Economies of Open Intelligence",
  },
  {
    label: "Dataset restrictions",
    value: "Source content often carries stricter terms than dataset releases",
    source: "Multimodal Data Provenance",
  },
  {
    label: "Real-use heterogeneity",
    value: "No single public conversation source generalizes across AI use",
    source: "AI Observatory",
  },
];

export const researchOutputs: ResearchOutput[] = [
  {
    year: "2026",
    title: "The AI Observatory: A Public Measure of Real-World AI Use",
    description:
      "A public measurement platform for real-use AI conversations, model behavior, and user interaction patterns.",
    href: AI_OBSERVATORY_PAPER_LINK,
    tag: "NeurIPS 2026",
  },
  {
    year: "2025",
    title:
      "Economies of Open Intelligence: Tracing Power and Participation in the Model Ecosystem",
    description:
      "An ecosystem-level study of concentration, participation, and transparency across open-weight AI models.",
    href: ECONOMIES_PAPER_LINK,
    tag: "Preprint",
  },
  {
    year: "2025",
    title: "Bridging the Data Provenance Gap Across Text, Speech, and Video",
    description:
      "An audit of four thousand public text, speech, and video datasets.",
    href: "/Multimodal_Data_Provenance.pdf",
    tag: "ICLR 2025",
  },
  {
    year: "2024",
    title: "Consent in Crisis: The Rapid Decline of the AI Data Commons",
    description:
      "An audit of terms, restrictions, and consent signals across the web.",
    href: "/Consent_in_Crisis.pdf",
    tag: "NeurIPS 2024",
  },
];

export const footerResearchLinks: ResearchLink[] = [
  { label: "Data Provenance Explorer", href: "/data-provenance-explorer" },
  { label: "Data Collection", href: DATA_REPO_LINK, external: true },
  {
    label: "Open Model Dashboard",
    href: OPEN_MODEL_DASHBOARD_LINK,
    external: true,
  },
  {
    label: "AI Observatory",
    href: AI_OBSERVATORY_LINK,
    external: true,
  },
  { label: "Publications", href: "/publications" },
];
